import type { App, Environment, Platform } from '@hangar/shared';
import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { OptionSelect } from '@/components/OptionSelect';
import { SCOPE_OPTIONS, needsApp, needsEnvironment, needsPlatform, type ScopeKey } from './envHelpers';

const PLATFORM_OPTIONS = [
  { value: 'android', label: 'Android' },
  { value: 'ios', label: 'iOS' },
];

export interface ScopeSelection {
  scope: ScopeKey;
  appSlug?: string;
  environment?: string;
  platform?: Platform;
  includeInherited?: boolean;
}

interface Props {
  value: ScopeSelection;
  onChange: (next: ScopeSelection) => void;
  apps: App[] | undefined;
  environments: Environment[];
  className?: string;
}

/** Scope selector: org / project / project+env / app / app+env / platform, plus the narrowing selects it needs. */
export function ScopePicker({ value, onChange, apps, environments, className }: Props) {
  const hint = SCOPE_OPTIONS.find((o) => o.value === value.scope)?.hint;
  return (
    <div className={cn('flex flex-wrap items-end gap-3', className)} data-testid="env-scope-picker">
      <div className="flex min-w-[220px] flex-col gap-1">
        <Label htmlFor="env-scope" className="text-xs text-subtle">
          Scope
        </Label>
        <OptionSelect id="env-scope" data-testid="env-scope" value={value.scope} onValueChange={(scope) => onChange({ ...value, scope: scope as ScopeKey })} options={SCOPE_OPTIONS} />
      </div>
      {needsApp(value.scope) ? <AppSelect value={value} onChange={onChange} apps={apps} /> : null}
      {needsEnvironment(value.scope) ? <EnvironmentSelect value={value} onChange={onChange} environments={environments} /> : null}
      {needsPlatform(value.scope) ? <PlatformSelect value={value} onChange={onChange} /> : null}
      {needsApp(value.scope) ? <InheritedSwitch value={value} onChange={onChange} /> : null}
      {hint ? <p className="basis-full text-xs text-subtle">{hint}</p> : null}
    </div>
  );
}

interface PartProps {
  value: ScopeSelection;
  onChange: (next: ScopeSelection) => void;
}

function AppSelect({ value, onChange, apps }: PartProps & { apps: App[] | undefined }) {
  return (
    <div className="flex min-w-[180px] flex-col gap-1">
      <Label htmlFor="env-scope-app" className="text-xs text-subtle">
        App
      </Label>
      <OptionSelect
        id="env-scope-app"
        data-testid="env-scope-app"
        value={value.appSlug ?? ''}
        onValueChange={(appSlug) => onChange({ ...value, appSlug })}
        placeholder="Pick an app"
        options={(apps ?? []).map((a) => ({ value: a.slug, label: a.slug }))}
        className="font-mono sm:text-[13px]"
        contentClassName="font-mono"
      />
    </div>
  );
}

function EnvironmentSelect({ value, onChange, environments }: PartProps & { environments: Environment[] }) {
  return (
    <div className="flex min-w-[160px] flex-col gap-1">
      <Label htmlFor="env-scope-environment" className="text-xs text-subtle">
        Environment
      </Label>
      <OptionSelect
        id="env-scope-environment"
        data-testid="env-scope-environment"
        value={value.environment ?? ''}
        onValueChange={(environment) => onChange({ ...value, environment })}
        placeholder="Pick an environment"
        options={environments.map((env) => ({ value: env.slug, label: env.name }))}
      />
    </div>
  );
}

function PlatformSelect({ value, onChange }: PartProps) {
  return (
    <div className="flex min-w-[140px] flex-col gap-1">
      <Label htmlFor="env-scope-platform" className="text-xs text-subtle">
        Platform
      </Label>
      <OptionSelect
        id="env-scope-platform"
        data-testid="env-scope-platform"
        value={value.platform ?? ''}
        onValueChange={(platform) => onChange({ ...value, platform: platform as Platform })}
        placeholder="Pick a platform"
        options={PLATFORM_OPTIONS}
      />
    </div>
  );
}

function InheritedSwitch({ value, onChange }: PartProps) {
  return (
    <label className="flex h-9 items-center gap-2 text-[13px] text-muted-foreground max-sm:h-11">
      <Switch size="sm" data-testid="env-scope-inherited" checked={value.includeInherited ?? false} onCheckedChange={(v) => onChange({ ...value, includeInherited: v })} />
      Include inherited
    </label>
  );
}
